<script setup>
import { ref, computed, reactive, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { required, helpers, url } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useToggle } from '@vueuse/core';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import AccessToken from 'dashboard/routes/dashboard/settings/profile/AccessToken.vue';

const props = defineProps({
  type: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  selectedBot: {
    type: Object,
    default: () => ({}),
  },
});

const MODAL_TYPES = { CREATE: 'create', EDIT: 'edit' };
const BOT_TYPES = { WEBHOOK: 'webhook', OPENAI: 'openai_inline' };

const store = useStore();
const { t } = useI18n();
const dialogRef = ref(null);
const uiFlags = useMapGetter('agentBots/getUIFlags');

const formState = reactive({
  botName: '',
  botDescription: '',
  botType: BOT_TYPES.WEBHOOK,
  botUrl: '',
  botAvatar: null,
  botAvatarUrl: '',
  systemPrompt: '',
  openaiApiKey: '',
  openaiModel: 'gpt-4o-mini',
});

const [showAccessToken, toggleAccessToken] = useToggle();
const accessToken = ref('');
const botSecret = ref('');

const isWebhookBot = computed(() => formState.botType === BOT_TYPES.WEBHOOK);
const isOpenAiBot = computed(() => formState.botType === BOT_TYPES.OPENAI);

const v$ = useVuelidate(
  {
    botName: {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.NAME'),
        required
      ),
    },
    botUrl: {
      requiredIfWebhook: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.URL'),
        value => !isWebhookBot.value || (value && value.length > 0)
      ),
      validUrl: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.VALID_URL'),
        value => !isWebhookBot.value || !value || url(value)
      ),
    },
    systemPrompt: {
      requiredIfOpenAi: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.SYSTEM_PROMPT'),
        value => !isOpenAiBot.value || (value && value.length > 0)
      ),
    },
    openaiApiKey: {
      requiredIfOpenAi: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.API_KEY'),
        value => !isOpenAiBot.value || (value && value.length > 0)
      ),
    },
  },
  formState
);

const isLoading = computed(() =>
  props.type === MODAL_TYPES.CREATE
    ? uiFlags.value.isCreating
    : uiFlags.value.isUpdating
);

const dialogTitle = computed(() => {
  if (showAccessToken.value) return t('AGENT_BOTS.ACCESS_TOKEN.TITLE');
  return props.type === MODAL_TYPES.CREATE
    ? t('AGENT_BOTS.ADD.TITLE')
    : t('AGENT_BOTS.EDIT.TITLE');
});

const dialogDescription = computed(() =>
  showAccessToken.value ? t('AGENT_BOTS.ACCESS_TOKEN.DESCRIPTION') : ''
);

const confirmButtonLabel = computed(() =>
  props.type === MODAL_TYPES.CREATE
    ? t('AGENT_BOTS.FORM.CREATE')
    : t('AGENT_BOTS.FORM.UPDATE')
);

const botNameError = computed(() =>
  v$.value.botName.$error ? v$.value.botName.$errors[0]?.$message : ''
);
const botUrlError = computed(() =>
  v$.value.botUrl.$error ? v$.value.botUrl.$errors[0]?.$message : ''
);
const systemPromptError = computed(() =>
  v$.value.systemPrompt.$error ? v$.value.systemPrompt.$errors[0]?.$message : ''
);
const openaiApiKeyError = computed(() =>
  v$.value.openaiApiKey.$error ? v$.value.openaiApiKey.$errors[0]?.$message : ''
);

const showAccessTokenInput = computed(
  () =>
    showAccessToken.value ||
    props.type === MODAL_TYPES.EDIT ||
    accessToken.value
);

const resetForm = () => {
  Object.assign(formState, {
    botName: '',
    botDescription: '',
    botType: BOT_TYPES.WEBHOOK,
    botUrl: '',
    botAvatar: null,
    botAvatarUrl: '',
    systemPrompt: '',
    openaiApiKey: '',
    openaiModel: 'gpt-4o-mini',
  });
  v$.value.$reset();
};

const handleImageUpload = ({ file, url: avatarUrl }) => {
  formState.botAvatar = file;
  formState.botAvatarUrl = avatarUrl;
};

const handleAvatarDelete = async () => {
  if (props.selectedBot?.id) {
    try {
      await store.dispatch(
        'agentBots/deleteAgentBotAvatar',
        props.selectedBot.id
      );
      formState.botAvatar = null;
      formState.botAvatarUrl = '';
      useAlert(t('AGENT_BOTS.AVATAR.SUCCESS_DELETE'));
    } catch {
      useAlert(t('AGENT_BOTS.AVATAR.ERROR_DELETE'));
    }
  } else {
    formState.botAvatar = null;
    formState.botAvatarUrl = '';
  }
};

const handleSubmit = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;
  if (showAccessToken.value) return;

  let botData;

  if (isOpenAiBot.value) {
    botData = {
      name: formState.botName,
      description: formState.botDescription,
      bot_type: BOT_TYPES.OPENAI,
      avatar: formState.botAvatar,
      bot_config: {
        system_prompt: formState.systemPrompt,
        openai_api_key: formState.openaiApiKey,
        model: formState.openaiModel || 'gpt-4o-mini',
      },
    };
  } else {
    botData = {
      name: formState.botName,
      description: formState.botDescription,
      outgoing_url: formState.botUrl,
      bot_type: BOT_TYPES.WEBHOOK,
      avatar: formState.botAvatar,
    };
  }

  const isCreate = props.type === MODAL_TYPES.CREATE;

  try {
    const actionPayload = isCreate
      ? botData
      : { id: props.selectedBot.id, data: botData };
    const response = await store.dispatch(
      `agentBots/${isCreate ? 'create' : 'update'}`,
      actionPayload
    );

    useAlert(
      isCreate
        ? t('AGENT_BOTS.ADD.API.SUCCESS_MESSAGE')
        : t('AGENT_BOTS.EDIT.API.SUCCESS_MESSAGE')
    );

    if (isCreate) {
      const {
        access_token: responseAccessToken,
        secret: responseSecret,
        id,
      } = response || {};
      if (id && responseAccessToken) {
        accessToken.value = responseAccessToken;
        botSecret.value = responseSecret || '';
        toggleAccessToken(true);
      } else {
        accessToken.value = '';
        botSecret.value = '';
        dialogRef.value.close();
      }
    } else {
      dialogRef.value.close();
    }

    resetForm();
  } catch {
    useAlert(
      isCreate
        ? t('AGENT_BOTS.ADD.API.ERROR_MESSAGE')
        : t('AGENT_BOTS.EDIT.API.ERROR_MESSAGE')
    );
  }
};

const initializeForm = () => {
  if (props.selectedBot && Object.keys(props.selectedBot).length) {
    const {
      name,
      description,
      outgoing_url: botUrl,
      bot_type: botType,
      thumbnail,
      bot_config: botConfig,
      access_token: botAccessToken,
      secret: botSecretValue,
    } = props.selectedBot;

    formState.botName = name || '';
    formState.botDescription = description || '';
    formState.botType = botType || BOT_TYPES.WEBHOOK;
    formState.botAvatarUrl = thumbnail || '';

    if (botType === BOT_TYPES.OPENAI) {
      formState.systemPrompt = botConfig?.system_prompt || '';
      formState.openaiApiKey = botConfig?.openai_api_key || '';
      formState.openaiModel = botConfig?.model || 'gpt-4o-mini';
      formState.botUrl = '';
    } else {
      formState.botUrl = botUrl || botConfig?.webhook_url || '';
    }

    if (props.type === MODAL_TYPES.EDIT) {
      if (botAccessToken) accessToken.value = botAccessToken;
      if (botSecretValue) botSecret.value = botSecretValue;
    }
  } else {
    resetForm();
  }
};

const onCopyToken = async value => {
  await copyTextToClipboard(value);
  useAlert(t('AGENT_BOTS.ACCESS_TOKEN.COPY_SUCCESSFUL'));
};

const onCopySecret = async value => {
  await copyTextToClipboard(value || botSecret.value);
  useAlert(t('AGENT_BOTS.SECRET.COPY_SUCCESS'));
};

const onResetSecret = async () => {
  const response = await store.dispatch(
    'agentBots/resetSecret',
    props.selectedBot.id
  );
  if (response) {
    botSecret.value = response.secret;
    useAlert(t('AGENT_BOTS.SECRET.RESET_SUCCESS'));
  } else {
    useAlert(t('AGENT_BOTS.SECRET.RESET_ERROR'));
  }
};

const onResetToken = async () => {
  const response = await store.dispatch(
    'agentBots/resetAccessToken',
    props.selectedBot.id
  );
  if (response) {
    accessToken.value = response.access_token;
    useAlert(t('AGENT_BOTS.ACCESS_TOKEN.RESET_SUCCESS'));
  } else {
    useAlert(t('AGENT_BOTS.ACCESS_TOKEN.RESET_ERROR'));
  }
};

const closeModal = () => {
  if (!showAccessToken.value) v$.value?.$reset();
  accessToken.value = '';
  botSecret.value = '';
  toggleAccessToken(false);
};

const onClickClose = () => {
  closeModal();
  dialogRef.value.close();
};

watch(() => props.selectedBot, initializeForm, { immediate: true, deep: true });

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="dialogTitle"
    :description="dialogDescription"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="closeModal"
  >
    <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
      <div
        v-if="!showAccessToken || type === MODAL_TYPES.EDIT"
        class="flex flex-col gap-4"
      >
        <!-- Avatar -->
        <div class="mb-2 flex flex-col items-start">
          <span class="mb-2 text-sm font-medium text-n-slate-12">
            {{ $t('AGENT_BOTS.FORM.AVATAR.LABEL') }}
          </span>
          <Avatar
            :src="formState.botAvatarUrl"
            :name="formState.botName"
            :size="68"
            allow-upload
            icon-name="i-lucide-bot-message-square"
            @upload="handleImageUpload"
            @delete="handleAvatarDelete"
          />
        </div>

        <!-- Bot Name -->
        <Input
          id="bot-name"
          v-model="formState.botName"
          :label="$t('AGENT_BOTS.FORM.NAME.LABEL')"
          :placeholder="$t('AGENT_BOTS.FORM.NAME.PLACEHOLDER')"
          :message="botNameError"
          :message-type="botNameError ? 'error' : 'info'"
          @blur="v$.botName.$touch()"
        />

        <!-- Description -->
        <TextArea
          id="bot-description"
          v-model="formState.botDescription"
          :label="$t('AGENT_BOTS.FORM.DESCRIPTION.LABEL')"
          :placeholder="$t('AGENT_BOTS.FORM.DESCRIPTION.PLACEHOLDER')"
        />

        <!-- Bot Type Selector -->
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('AGENT_BOTS.FORM.BOT_TYPE.LABEL') }}
          </label>
          <div class="flex gap-3">
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="formState.botType"
                type="radio"
                value="webhook"
                class="accent-woot-500"
              />
              <span class="text-sm text-n-slate-12">{{
                $t('AGENT_BOTS.FORM.BOT_TYPE.WEBHOOK')
              }}</span>
            </label>
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="formState.botType"
                type="radio"
                value="openai_inline"
                class="accent-woot-500"
              />
              <span class="text-sm text-n-slate-12">{{
                $t('AGENT_BOTS.FORM.BOT_TYPE.OPENAI')
              }}</span>
            </label>
          </div>
        </div>

        <!-- Webhook URL (solo para tipo webhook) -->
        <Input
          v-if="isWebhookBot"
          id="bot-url"
          v-model="formState.botUrl"
          :label="$t('AGENT_BOTS.FORM.WEBHOOK_URL.LABEL')"
          :placeholder="$t('AGENT_BOTS.FORM.WEBHOOK_URL.PLACEHOLDER')"
          :message="botUrlError"
          :message-type="botUrlError ? 'error' : 'info'"
          @blur="v$.botUrl.$touch()"
        />

        <!-- OpenAI fields (solo para tipo openai_inline) -->
        <template v-if="isOpenAiBot">
          <Input
            id="openai-api-key"
            v-model="formState.openaiApiKey"
            :label="$t('AGENT_BOTS.FORM.OPENAI_API_KEY.LABEL')"
            :placeholder="$t('AGENT_BOTS.FORM.OPENAI_API_KEY.PLACEHOLDER')"
            type="password"
            :message="openaiApiKeyError"
            :message-type="openaiApiKeyError ? 'error' : 'info'"
            @blur="v$.openaiApiKey.$touch()"
          />

          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">
              {{ $t('AGENT_BOTS.FORM.OPENAI_MODEL.LABEL') }}
            </label>
            <select
              v-model="formState.openaiModel"
              class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:border-woot-500 focus:outline-none"
            >
              <option value="gpt-4o-mini">
                {{ $t('AGENT_BOTS.FORM.OPENAI_MODEL.OPTIONS.GPT4O_MINI') }}
              </option>
              <option value="gpt-4o">
                {{ $t('AGENT_BOTS.FORM.OPENAI_MODEL.OPTIONS.GPT4O') }}
              </option>
              <option value="gpt-4-turbo">
                {{ $t('AGENT_BOTS.FORM.OPENAI_MODEL.OPTIONS.GPT4_TURBO') }}
              </option>
              <option value="gpt-3.5-turbo">
                {{ $t('AGENT_BOTS.FORM.OPENAI_MODEL.OPTIONS.GPT35_TURBO') }}
              </option>
            </select>
          </div>

          <TextArea
            id="system-prompt"
            v-model="formState.systemPrompt"
            :label="$t('AGENT_BOTS.FORM.SYSTEM_PROMPT.LABEL')"
            :placeholder="$t('AGENT_BOTS.FORM.SYSTEM_PROMPT.PLACEHOLDER')"
            rows="10"
            :message="systemPromptError"
            :message-type="systemPromptError ? 'error' : 'info'"
            @blur="v$.systemPrompt.$touch()"
          />

          <div class="rounded-lg bg-n-alpha-2 p-3 text-xs text-n-slate-11">
            {{ $t('AGENT_BOTS.OPENAI.HANDOFF_INFO') }}
          </div>
        </template>
      </div>

      <!-- Webhook Secret (edit mode) -->
      <div
        v-if="botSecret && type === MODAL_TYPES.EDIT && isWebhookBot"
        class="flex flex-col gap-1"
      >
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ $t('AGENT_BOTS.SECRET.LABEL') }}
        </label>
        <AccessToken
          :value="botSecret"
          @on-copy="onCopySecret"
          @on-reset="onResetSecret"
        />
      </div>

      <!-- Access Token -->
      <div v-if="showAccessTokenInput" class="flex flex-col gap-1">
        <label
          v-if="type === MODAL_TYPES.EDIT"
          class="mb-0.5 text-sm font-medium text-n-slate-12"
        >
          {{ $t('AGENT_BOTS.ACCESS_TOKEN.TITLE') }}
        </label>
        <AccessToken
          v-if="type === MODAL_TYPES.EDIT"
          :value="accessToken"
          @on-copy="onCopyToken"
          @on-reset="onResetToken"
        />
        <AccessToken
          v-else
          :value="accessToken"
          :show-reset-button="false"
          @on-copy="onCopyToken"
        />
      </div>

      <!-- Secret after creation -->
      <div
        v-if="
          botSecret &&
          showAccessToken &&
          type === MODAL_TYPES.CREATE &&
          isWebhookBot
        "
        class="flex flex-col gap-1"
      >
        <p class="text-sm text-n-slate-11">
          {{ $t('AGENT_BOTS.SECRET.CREATED_DESC') }}
        </p>
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ $t('AGENT_BOTS.SECRET.LABEL') }}
        </label>
        <AccessToken
          :value="botSecret"
          :show-reset-button="false"
          @on-copy="onCopySecret"
        />
      </div>

      <!-- Actions -->
      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('AGENT_BOTS.FORM.CANCEL')"
          @click="onClickClose()"
        />
        <NextButton
          v-if="!showAccessToken"
          type="submit"
          data-testid="label-submit"
          :label="confirmButtonLabel"
          :is-loading="isLoading"
          :disabled="v$.$invalid"
        />
      </div>
    </form>
  </Dialog>
</template>
